import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, ChevronLeft, ChevronDown, ChevronUp, Loader2, Plus, TriangleAlert, Users, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { ActionButton, EmptyState, Field, SecondaryAction } from "../kit";
import LoadFailedCard from "./LoadFailedCard";
import TagSwitch from "./TagSwitch";
import useCoachGame from "../../hooks/useCoachGame";
import { saveSheet } from "../../lib/coachGames";
import { track } from "../../lib/usageTracker";
import { squadFiguresLine, bowlingFiguresLine } from "../../lib/pickXiFigures";
import { fetchPlayerSummaries } from "../../lib/squadAverage";
import * as sheet from "../../lib/pickXiSheet";

const GAMES_PATH = "/coach-portal/games";
const VOLUNTEER_MAX = 80;
const WANTED_BOWLERS = 5;

/**
 * Issue #1317 (Pick XI coach 2 of 3; D297, D300, D302, D309) — Pick the XI,
 * step 1 of 2, drawn in `PickTheXI.dc.html` (round 13): the batting order with
 * captain, vice-captain and keepers, the 12th man, the bench, the bowling
 * order, the match-day volunteers, and Next.
 *
 * The state is `lib/pickXiSheet.js`; this file only draws it. Every control
 * that changes the list (tags, arrows, remove, Full/Add, 12th, the bowler
 * chips) acts in place, so G1 counts none of them (D253), and nothing blocks
 * Next: the checklist is the warning. Next saves the draft with
 * `PUT /coach-games/:id/sheet` and goes to `?step=plan` (#1318 builds that
 * step; until then the same sheet shows there).
 *
 * States, each its own (D53): `loading`, `failed` (Try again), `empty` (no
 * squad), and the sheet, which carries `part-picked`, `missing-tags`,
 * `short-squad` and `save-failed`. A failed save keeps everything on screen
 * (D57).
 *
 * The volunteers are free text for the coach's own view (D300); players never
 * see them.
 */
export default function PickTheXI({ gameId, source = null, besideList }) {
  const navigate = useNavigate();
  // #745: Team selection reads the game once to choose between this screen and On the day, and
  // hands that read in as `source`; opened on its own, this reads the game itself.
  const own = useCoachGame(gameId, { skip: !!source });
  const src = source || own;
  const [state, setState] = useState(() => (src.status === "ready" ? sheet.fromServer(src.game) : null));
  const seeded = useRef(src.status === "ready" ? src.game : null);
  const [summaries, setSummaries] = useState(null); // { [playerId]: { ok, data } } | null while loading
  const [saveState, setSaveState] = useState("idle"); // idle | saving | failed
  const load = { status: src.status === "ready" && state ? "ready" : src.status === "failed" ? "failed" : "loading" }; // loading | failed | ready

  useEffect(() => {
    if (src.status !== "ready") {
      setSummaries(null);
      return undefined;
    }
    let live = true;
    if (seeded.current !== src.game) {
      seeded.current = src.game;
      setState(sheet.fromServer(src.game));
    }
    setSummaries(null);
    // Each player's figures, 4 at a time, never blocking the rows.
    fetchPlayerSummaries((src.game?.squad || []).map((m) => m.player_id)).then((results) => {
      if (!live) return;
      const byId = {};
      for (const res of results) byId[res.playerId] = res;
      setSummaries(byId);
    });
    return () => {
      live = false;
    };
  }, [src.status, src.game]);

  // One reducer call, applied to the latest state. Any edit clears a failed-save notice.
  const act = useCallback((fn, ...args) => {
    setState((s) => fn(s, ...args));
    setSaveState((v) => (v === "failed" ? "idle" : v));
  }, []);

  const next = async () => {
    if (saveState === "saving") return;
    setSaveState("saving");
    try {
      await saveSheet(gameId, sheet.toBody(state));
      if (sheet.isFull(state)) track("xi.picked", { item: "complete" });
      else track("xi.picked", { item: "partial" });
      navigate(`/coach-portal/team?game=${encodeURIComponent(gameId)}&step=plan`);
      setSaveState("idle");
    } catch (e) {
      console.error("PickTheXI: failed to save the team sheet", e);
      setSaveState("failed");
    }
  };

  const back = (
    <SecondaryAction as={Link} to={GAMES_PATH} variant="link" data-testid="xi-back" style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}>
      <ChevronLeft size={16} aria-hidden="true" />
      Your games
    </SecondaryAction>
  );
  // One stable place for the way back: the same element at the same position in the tree
  // across loading, failed, empty and ready, so a tap that lands as the sheet swaps in is not
  // lost, and a reader of the DOM never holds a copy that is about to go (#1318 review F2).
  let body;
  if (load.status === "loading") body = <Skeleton />;
  else if (load.status === "failed") {
    body = <LoadFailedCard title="Couldn't load the squad" testId="xi-failed" retryTestId="xi-failed-retry" onRetry={src.reload} />;
  } else if (state.squad.length === 0) {
    body = (
      <div className="flex flex-col" style={{ gap: 10 }}>
        <EmptyState
          shape="portal"
          data-testid="xi-empty"
          icon={<Users size={26} strokeWidth={2} />}
          line="Nobody is in this game's squad yet"
          prose="Tick the players for this game, then pick the eleven."
        />
        <EditSquadLink gameId={gameId} testId="xi-edit-squad-empty" />
      </div>
    );
  } else {
    body = <Sheet state={state} summaries={summaries} act={act} gameId={gameId} saveState={saveState} onNext={next} />;
  }

  return (
    <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
      {/* #1430: Your games is beside this sheet, so the way back to it is not drawn. */}
      {!besideList && back}
      {body}
    </div>
  );
}

function Skeleton() {
  const bar = (w, c) => <div style={{ width: w, height: 12, borderRadius: 6, background: c }} />;
  return (
    <div data-testid="xi-loading" aria-busy="true" className="flex flex-col" style={{ gap: 6 }}>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} style={{ display: "flex", flexDirection: "column", gap: 8, padding: "12px 14px", height: 92, boxSizing: "border-box", borderRadius: "var(--nw-radius-card)", background: "var(--nw-surface)", border: "1px solid var(--nw-line)" }}>
          {bar("50%", "var(--nw-line)")}
          {bar("30%", "var(--nw-line-faint)")}
        </div>
      ))}
    </div>
  );
}

function EditSquadLink({ gameId, testId }) {
  return (
    <SecondaryAction as={Link} to={`/coach-portal/games/schedule?game=${encodeURIComponent(gameId)}`} variant="link" data-testid={testId} style={{ display: "flex", width: "fit-content", alignItems: "center" }}>
      Edit the squad
    </SecondaryAction>
  );
}

function Sheet({ state, summaries, act, gameId, saveState, onNext }) {
  const list = sheet.checklist(state);
  const nameOf = useMemo(() => Object.fromEntries(state.squad.map((m) => [m.player_id, m.full_name])), [state.squad]);
  const benchList = sheet.bench(state);
  const full = sheet.isFull(state);
  const short = state.squad.length < sheet.XI_SIZE;
  const figures = (id) => (summaries ? summaries[id] : null);
  const batPlace = (id) => state.xi.findIndex((e) => e.id === id) + 1;
  const notBowling = state.xi.filter((e) => !state.bowling.includes(e.id));

  return (
    <div data-testid="xi-pick" className="flex flex-col" style={{ gap: 14 }}>
      <div className="flex flex-col" style={{ gap: 4 }}>
        <div className="nw-eyebrow">Pick the XI</div>
        <div className="nw-title-lg" data-testid="xi-title">Batting &amp; bowling order</div>
        <div className="nw-lede">Set the batting order 1–11, tag your captain, vice-captain and keeper(s), then set who opens the bowling.</div>
      </div>

      <PickedCard list={list} />
      {list.picked > 0 && (!list.captain || !list.keeper) && (
        <div data-testid="xi-picked-missing" className="nw-help">Tag a captain and at least one keeper. You can go on to the game plan and come back.</div>
      )}
      {short && (
        <div data-testid="xi-picked-short" className="flex flex-col" style={{ gap: 2 }}>
          <div className="nw-help">
            {state.squad.length === 1 ? "Only 1 player is" : `Only ${state.squad.length} players are`} in this game's squad, so {state.squad.length === sheet.XI_SIZE - 1 ? `slot ${sheet.XI_SIZE}` : `slots ${state.squad.length + 1} to ${sheet.XI_SIZE}`} stay{state.squad.length === sheet.XI_SIZE - 1 ? "s" : ""} open. Add a player to the squad from the game's details.
          </div>
          <EditSquadLink gameId={gameId} testId="xi-edit-squad" />
        </div>
      )}

      <SectionHead label="Batting order" labelId="xi-bat-label" aside="Tap tags to assign" />
      <Legend />
      <div className="flex flex-col" style={{ gap: 6 }}>
        {state.xi.map((e, i) => {
          const n = i + 1;
          const name = nameOf[e.id];
          return (
            <div
              key={e.id}
              data-testid={`xi-bat-${n}`}
              style={{ display: "flex", flexDirection: "column", gap: 8, padding: "10px 4px 12px 12px", background: "var(--nw-surface)", border: "1px solid", borderColor: e.captain ? "var(--nw-navy)" : "var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
                <Badge>{n}</Badge>
                <Who name={name} paddingTop={2}>
                  <PlayerFigures summary={figures(e.id)} />
                </Who>
                <div style={{ display: "flex", flexShrink: 0 }}>
                  <IconBtn testId={`xi-bat-up-${n}`} label={`Move ${name} up`} disabled={n === 1} onClick={() => act(sheet.moveUp, n)} icon={ChevronUp} />
                  <IconBtn testId={`xi-bat-down-${n}`} label={`Move ${name} down`} disabled={n === state.xi.length} onClick={() => act(sheet.moveDown, n)} icon={ChevronDown} />
                  <IconBtn testId={`xi-bat-remove-${n}`} label={`Remove ${name}`} onClick={() => act(sheet.remove, n)} icon={X} />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, paddingLeft: 42 }}>
                <TagSwitch kind="c" text="C" label={`Captain: ${name}`} on={e.captain} onToggle={() => act(sheet.toggleCaptain, n)} data-testid={`xi-tag-c-${n}`} />
                <TagSwitch kind="vc" text="VC" label={`Vice-captain: ${name}`} on={e.vice} onToggle={() => act(sheet.toggleVice, n)} data-testid={`xi-tag-vc-${n}`} />
                <TagSwitch kind="wk" text="WK" label={`Wicket keeper: ${name}`} on={e.keeper} onToggle={() => act(sheet.toggleKeeper, n)} data-testid={`xi-tag-wk-${n}`} />
              </div>
            </div>
          );
        })}
        {!full && (
          <div
            data-testid={`xi-bat-empty-${state.xi.length + 1}`}
            style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 56, padding: "8px 12px", boxSizing: "border-box", background: "var(--nw-page)", border: "1px dashed var(--nw-line-strong)", borderRadius: "var(--nw-radius-card)" }}
          >
            <Badge muted>{state.xi.length + 1}</Badge>
            <div className="flex flex-col" style={{ gap: 2 }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink-faint)" }}>Not filled</span>
              <span className="nw-help">Add from the bench below</span>
            </div>
          </div>
        )}
      </div>

      <SectionHead label="12th man" labelId="xi-twelfth-label" aside="Fields only" />
      {state.twelfth ? (
        <div data-testid="xi-twelfth" style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px 8px 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
          <Badge tint>12</Badge>
          <Who name={nameOf[state.twelfth]}>
            <PlayerFigures summary={figures(state.twelfth)} />
          </Who>
          <IconBtn testId="xi-twelfth-remove" label={`Remove ${nameOf[state.twelfth]}`} onClick={() => act(sheet.clearTwelfth)} icon={X} />
        </div>
      ) : (
        <div
          data-testid="xi-twelfth-empty"
          style={{ display: "flex", alignItems: "center", gap: 10, minHeight: 56, padding: "8px 12px", boxSizing: "border-box", background: "var(--nw-page)", border: "1px dashed var(--nw-line-strong)", borderRadius: "var(--nw-radius-card)" }}
        >
          <Badge muted>12</Badge>
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: "var(--nw-ink-faint)" }}>Not filled</span>
            <span className="nw-help">Add from the bench below</span>
          </div>
        </div>
      )}

      <SectionHead label="Bench · rest of squad" labelId="xi-bench-label" aside={`${benchList.length} available`} />
      <div className="flex flex-col" style={{ gap: 6 }}>
        {benchList.map((m) => {
          const id = m.player_id;
          return (
            <div key={id} data-testid={`xi-bench-${id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 8px 8px 14px", background: "var(--nw-page)", border: "1px dashed var(--nw-line-strong)", borderRadius: "var(--nw-radius-card)" }}>
              <Who name={m.full_name}>
                <PlayerFigures summary={figures(id)} />
              </Who>
              <PillBtn testId={`xi-bench-${id}-full`} label={`${full ? "Full" : "Add"}: ${m.full_name}`} disabled={full} primary={!full} onClick={() => act(sheet.addToXi, id)}>
                {full ? "Full" : "Add"}
              </PillBtn>
              <PillBtn testId={`xi-bench-${id}-12th`} label={`12th man: ${m.full_name}`} onClick={() => act(sheet.setTwelfth, id)}>
                12th
              </PillBtn>
            </div>
          );
        })}
      </div>
      {full && benchList.length > 0 && <div className="nw-help">The XI is full. Remove someone from the batting order to bring a bench player in.</div>}

      <SectionHead label="Bowling order" labelId="xi-bowl-label" aside={`${list.bowlers} ${list.bowlers === 1 ? "bowler" : "bowlers"}`} />
      <div className="nw-lede">Anyone in the XI can bowl. 1 and 2 open the bowling.</div>
      <div className="flex flex-col" style={{ gap: 6 }}>
        {state.bowling.map((id, i) => {
          const n = i + 1;
          const name = nameOf[id];
          const res = figures(id);
          return (
            <div key={id} data-testid={`xi-bowl-${n}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 4px 8px 12px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
              <Badge round>{n}</Badge>
              <Who name={name}>
                <span className="nw-help">{bowlingFiguresLine(res && res.ok ? res.data : null, { opening: n <= 2, batsAt: batPlace(id) })}</span>
              </Who>
              <div style={{ display: "flex", flexShrink: 0 }}>
                <IconBtn testId={`xi-bowl-up-${n}`} label={`Move ${name} up the bowling order`} disabled={n === 1} onClick={() => act(sheet.moveBowler, n, -1)} icon={ChevronUp} />
                <IconBtn testId={`xi-bowl-down-${n}`} label={`Move ${name} down the bowling order`} disabled={n === state.bowling.length} onClick={() => act(sheet.moveBowler, n, 1)} icon={ChevronDown} />
                <IconBtn testId={`xi-bowl-remove-${n}`} label={`Remove ${name} from the bowling order`} onClick={() => act(sheet.removeBowler, n)} icon={X} />
              </div>
            </div>
          );
        })}
      </div>
      {notBowling.length > 0 && (
        <>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--nw-ink)" }}>Add a bowler from the XI</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {notBowling.map((e) => (
              <button
                key={e.id}
                type="button"
                data-testid={`xi-add-bowler-${e.id}`}
                data-nav="in-place"
                onClick={() => act(sheet.addBowler, e.id)}
                style={{ display: "inline-flex", alignItems: "center", gap: 6, minHeight: 44, boxSizing: "border-box", padding: "0 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-pill)", fontFamily: "'DM Sans', 'Segoe UI', sans-serif", fontSize: 14, fontWeight: 600, color: "var(--nw-ink)", cursor: "pointer" }}
              >
                <Plus size={16} aria-hidden="true" />
                {nameOf[e.id]}
              </button>
            ))}
          </div>
        </>
      )}

      <SectionHead label="Match-day volunteers" labelId="xi-vol-label" />
      <div className="nw-help">Parents or club members helping out. Any name, they don't need to be in the squad.</div>
      <Volunteer label="Scoring support" testId="xi-vol-scoring" value={state.volunteers.scorer} onChange={(v) => act(sheet.setVolunteer, "scorer", v)} />
      <Volunteer label="Umpire 1" testId="xi-vol-umpire-1" value={state.volunteers.umpire_1} onChange={(v) => act(sheet.setVolunteer, "umpire_1", v)} />
      <Volunteer label="Umpire 2" testId="xi-vol-umpire-2" value={state.volunteers.umpire_2} onChange={(v) => act(sheet.setVolunteer, "umpire_2", v)} />

      {saveState === "failed" && (
        <div
          data-testid="xi-save-failed"
          role="alert"
          style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px", background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderLeft: "3px solid var(--nw-alert)", borderRadius: "var(--nw-radius-control)" }}
        >
          <TriangleAlert size={18} color="var(--nw-alert)" aria-hidden="true" style={{ flexShrink: 0, marginTop: 1 }} />
          <div className="flex flex-col" style={{ gap: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4, color: "var(--nw-ink)" }}>Couldn't save the team sheet</span>
            <span className="nw-help">Your order, tags and volunteers are still here. Try again.</span>
          </div>
        </div>
      )}

      <ActionButton
        data-testid="xi-next"
        onClick={onNext}
        disabled={saveState === "saving"}
        aria-busy={saveState === "saving" ? "true" : undefined}
        icon={saveState === "saving" ? <Loader2 size={18} style={{ color: "#475569" }} className="animate-spin" /> : <ArrowRight size={18} strokeWidth={2.4} />}
        style={saveState === "saving" ? { background: "#E2E8F0", color: "#475569", opacity: 1 } : { color: "#0F172A" }}
      >
        {saveState === "saving" ? "Saving…" : "Next: game plan & notes"}
      </ActionButton>
      <div style={{ fontSize: 13, textAlign: "center", color: "var(--nw-ink-muted)" }}>Step 1 of 2. Add a game plan, then publish.</div>
    </div>
  );
}

function SectionHead({ label, labelId, aside = null }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
      <div className="nw-section" data-testid={labelId}>{label}</div>
      {aside && <div className="nw-caption-strong" style={{ color: "var(--nw-navy)" }}>{aside}</div>}
    </div>
  );
}

function Legend() {
  const chip = (style, text) => (
    <span style={{ display: "inline-flex", alignItems: "center", padding: "1px 6px", borderRadius: 6, fontSize: 11, fontWeight: 700, ...style }}>{text}</span>
  );
  const item = (chipEl, words) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      {chipEl} {words}
    </span>
  );
  return (
    <div data-testid="xi-legend" style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "6px 14px", fontSize: 13, color: "var(--nw-ink-lede)" }}>
      {item(chip({ background: "var(--nw-navy)", color: "#FFFFFF" }, "C"), "Captain (one)")}
      {item(chip({ background: "var(--nw-navy-tint)", color: "var(--nw-navy)", border: "1px solid var(--nw-navy)" }, "VC"), "Vice-captain (one)")}
      {item(chip({ background: "var(--nw-ok)", color: "#FFFFFF" }, "WK"), "Keeper (one or more)")}
    </div>
  );
}

/** "Picked N of 11" with the 11-segment bar and the four checklist chips. */
function PickedCard({ list }) {
  const bowlersOk = list.bowlers >= WANTED_BOWLERS;
  return (
    <div data-testid="xi-picked" style={{ display: "flex", flexDirection: "column", gap: 10, padding: 14, background: "var(--nw-surface)", border: "1px solid var(--nw-line)", borderRadius: "var(--nw-radius-card)" }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <span className="nw-section">Picked</span>
        <span className="nw-figure" data-testid="xi-picked-count">{`${list.picked} of ${list.of}`}</span>
      </div>
      <div data-testid={list.picked < list.of ? "xi-picked-part" : undefined} aria-hidden="true" style={{ display: "flex", gap: 3 }}>
        {Array.from({ length: list.of }, (_, i) => (
          <span key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: i < list.picked ? "var(--nw-navy)" : "var(--nw-line)" }} />
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 6 }}>
        <Chip ok={list.captain} alert>{list.captain ? "Captain set" : "No captain yet"}</Chip>
        <Chip ok={list.vice}>{list.vice ? "Vice-captain set" : "No vice-captain yet"}</Chip>
        <Chip ok={list.keeper} alert>{list.keeper ? "Keeper set" : "No keeper yet"}</Chip>
        <Chip ok={bowlersOk} alert>{`${list.bowlers} ${list.bowlers === 1 ? "bowler" : "bowlers"}`}</Chip>
      </div>
    </div>
  );
}

/** A checklist chip: done is the tint with a tick; not done is neutral, or the alert outline when `alert`. */
function Chip({ ok, alert = false, children }) {
  const warn = !ok && alert;
  return (
    <span
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 36,
        padding: "0 10px",
        boxSizing: "border-box",
        background: ok ? "var(--nw-navy-tint)" : "var(--nw-surface)",
        border: ok ? "1px solid transparent" : `1px solid ${warn ? "var(--nw-alert)" : "var(--nw-line-strong)"}`,
        borderRadius: 8,
        fontSize: 13,
        fontWeight: 600,
        color: ok ? "var(--nw-navy)" : warn ? "var(--nw-alert)" : "var(--nw-ink-lede)",
      }}
    >
      {ok ? <Check size={14} strokeWidth={3} aria-hidden="true" /> : <TriangleAlert size={14} aria-hidden="true" />}
      {children}
    </span>
  );
}

function Badge({ children, muted = false, tint = false, round = false }) {
  const base = { flexShrink: 0, width: 32, height: 32, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 };
  let look = { background: "var(--nw-navy)", color: "#FFFFFF", borderRadius: 8 };
  if (muted) look = { background: "var(--nw-line-faint)", color: "var(--nw-ink-faint)", borderRadius: 8 };
  if (tint) look = { background: "var(--nw-navy-tint)", color: "var(--nw-navy)", border: "1px solid var(--nw-navy)", borderRadius: 8 };
  if (round) look = { background: "var(--nw-surface)", color: "var(--nw-navy)", border: "2px solid var(--nw-navy)", borderRadius: "var(--nw-radius-pill)" };
  return <span style={{ ...base, ...look }}>{children}</span>;
}

function Who({ name, children, paddingTop = 0 }) {
  return (
    <span style={{ display: "flex", flexDirection: "column", gap: 2, flexGrow: 1, minWidth: 0, paddingTop }}>
      <span className="nw-rowname" style={{ overflowWrap: "anywhere" }}>{name}</span>
      {children}
    </span>
  );
}

/** A player's batting figures under their name: a skeleton while loading, a short note if the read failed. */
function PlayerFigures({ summary }) {
  if (!summary) return <span aria-hidden="true" style={{ display: "inline-block", height: 12, width: "55%", borderRadius: 6, background: "var(--nw-line)" }} />;
  if (!summary.ok) return <span className="nw-help">Couldn't load this player's figures</span>;
  return <span className="nw-help">{squadFiguresLine(summary.data)}</span>;
}

/** Up, down or remove: an icon in a 44px square that acts in place. An end of the list stays put (`aria-disabled`, still reachable). */
function IconBtn({ testId, label, icon: Icon, onClick, disabled = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-nav="in-place"
      aria-label={label}
      aria-disabled={disabled ? "true" : undefined}
      onClick={disabled ? undefined : onClick}
      style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", justifyContent: "center", width: 44, height: 44, padding: 0, background: "transparent", border: "none", color: disabled ? "var(--nw-ink-faint)" : "var(--nw-ink-lede)", cursor: disabled ? "not-allowed" : "pointer" }}
    >
      <Icon size={20} strokeWidth={2} aria-hidden="true" />
    </button>
  );
}

/** Full / Add and 12th on a bench row. A `Full` button stays present but inert while the XI is full (S3, as `CoachSquad`'s Team selection). */
function PillBtn({ testId, label, children, onClick, disabled = false, primary = false }) {
  return (
    <button
      type="button"
      data-testid={testId}
      data-nav="in-place"
      aria-label={label}
      aria-disabled={disabled ? "true" : undefined}
      onClick={disabled ? undefined : onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: 56,
        minHeight: 44,
        padding: "0 10px",
        boxSizing: "border-box",
        borderRadius: "var(--nw-radius-control)",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontSize: 14,
        fontWeight: 700,
        background: "var(--nw-surface)",
        color: disabled ? "var(--nw-ink-faint)" : primary ? "var(--nw-navy)" : "var(--nw-ink)",
        border: `1px solid ${disabled ? "var(--nw-line)" : primary ? "var(--nw-navy)" : "var(--nw-line-strong)"}`,
        cursor: disabled ? "not-allowed" : "pointer",
      }}
    >
      {children}
    </button>
  );
}

function Volunteer({ label, testId, value, onChange }) {
  return (
    <Field
      label={label}
      placeholder="Volunteer name"
      data-testid={testId}
      value={value}
      maxLength={VOLUNTEER_MAX}
      autoComplete="off"
      onChange={(e) => onChange(e.target.value)}
      inputStyle={{ fontSize: 15 }}
    />
  );
}
